import useCustomMutation from "@/composables/useCustomMutation";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useCompanyOpenPayCardsStore } from "@/stores/companyOpenPayCards";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { MaybeRef, watch } from "vue";
import { useToast } from "vue-toastification";

interface Params {
  companyId: MaybeRef<number | string | undefined>;
}
interface Props {
  params: Params;
  immediate?: boolean;
}

interface CreateParams extends Params {
  cardId: string;
  deviceId: string;
  userId?: string;
  alias?: string;
}

const getCards = async (params?: Params) => {
  if (!params) return;
  const { data } = await axios.get(
    `api/companies/${get(params.companyId)}/cards`,
  );
  return data;
};

const createCard = async (params?: CreateParams) => {
  if (!params) return;
  const { data } = await axios.post(
    `api/companies/${get(params.companyId)}/cards`,
    params,
  );
  return data;
};

const useCompanyOpenPayCard = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const { setCards } = useCompanyOpenPayCardsStore();
  const { cards } = storeToRefs(useCompanyOpenPayCardsStore());

  const { data, isLoading, refetch, isFetching, isError } = useQuery(
    ["companyCards", params?.companyId],
    () => getCards({ ...params! }),
    {
      enabled: params ? immediate : false,
      retry: false,
    },
  );

  const createMutation = useMutation(
    (MutationParams: MutationParams<CreateParams>) =>
      createCard(MutationParams.params),
    useCustomMutation(toast, "create"),
  );

  watch(data, () => {
    if (data.value) {
      setCards(data.value);
    }
  });

  return {
    cards,
    isLoading,
    refetch,
    isFetching,
    isError,

    createCardMutation: createMutation,
    createCard: createMutation.mutate,
    createCardAsync: createMutation.mutateAsync,
    createCardLoading: createMutation.isLoading,
  };
};

export default useCompanyOpenPayCard;
